import { v4 as uuid } from 'uuid';
import { logger } from '#~/logger/index.js';

/** Closed Add-flow boundaries; raw exception names/messages never become diagnostics. */
export type AddFlowOperation =
  | 'camera-auth'
  | 'camera-link-details'
  | 'camera-link-prepare'
  | 'camera-status'
  | 'auth-access-token'
  | 'media-token'
  | 'cloud-link-agent'
  | 'preview-prepare'
  | 'preview-remove'
  | 'preview-receive'
  | 'device-add-prepare'
  | 'device-mutation'
  | 'device-save'
  | 'unknown';

export type AddFlowOutcome = {
  operation: AddFlowOperation;
  category:
    | 'success'
    | 'cancelled'
    | 'superseded'
    | 'timeout'
    | 'unreachable'
    | 'refused'
    | 'locked'
    | 'sign-in'
    | 'missing-link'
    | 'unsupported'
    | 'service'
    | 'bridge-not-ready'
    | 'no-frame'
    | 'unknown';
  retry: 'none' | 'retry' | 'wait' | 'check-state';
  status?: number | undefined;
  deviceId?: string | undefined;
};

/** Only known service statuses are safe structured evidence (never arbitrary codes). */
export const safeAddFlowStatus = (status: number | undefined): number | undefined =>
  status !== undefined &&
  [400, 401, 403, 404, 408, 409, 422, 429, 500, 502, 503, 504].includes(status)
    ? status
    : undefined;

/** Structured status survives bounded cause wrappers without retaining their private text. */
export const addFlowStatusFromCause = (cause: unknown): number | undefined => {
  const seen = new Set<unknown>();
  for (let depth = 0; depth < 8 && cause && !seen.has(cause); depth += 1) {
    seen.add(cause);
    if (typeof cause !== 'object') break;
    const node = cause as { code?: unknown; cause?: unknown };
    const status = safeAddFlowStatus(typeof node.code === 'number' ? node.code : undefined);
    if (status !== undefined) return status;
    cause = node.cause;
  }
  return undefined;
};

/** One terminal presentation per owned attempt; late raw settlement cannot double-report it. */
export const createAddFlowDiagnostic = (context: {
  reference?: string;
  actor: 'client' | 'selected-bridge';
  sourceId?: string | undefined;
  agentId?: string | undefined;
  roomId?: string | undefined;
}) => {
  const reference = context.reference ?? uuid();
  const started = Date.now();
  let reported = false;
  return {
    reference,
    report(outcome: AddFlowOutcome): void {
      if (reported) return;
      reported = true;
      // Explicit fields prevent accidental spreading of a source URL, credentials or raw cause.
      logger.info('device add outcome', {
        attemptReference: reference,
        operation: outcome.operation,
        actor: context.actor,
        sourceId: context.sourceId,
        agentId: context.agentId,
        roomId: context.roomId,
        deviceId: outcome.deviceId,
        durationMs: Math.max(0, Date.now() - started),
        category: outcome.category,
        status: safeAddFlowStatus(outcome.status),
        retry: outcome.retry
      });
    }
  };
};
